import { Component, Input, OnInit, } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationWithName, PickupArea } from 'app/models/locations';
import { Hold } from 'user/models/holds';
import { TranslateService } from '@ngx-translate/core';
import { PickupLocationService } from 'user/services/pickup-location.service';
import { IlsType } from 'shared/models/ilsType';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { FeatureToggleService } from '../../../services/feature-toggle.service';

interface HoldLocationForm {
  pickupLocation: FormControl<string>;
  pickupArea: FormControl<string>;
}

@Component({
  selector: 'app-change-hold-location-dialog',
  templateUrl: './change-hold-location-dialog.component.html',
  styleUrls: ['./change-hold-location-dialog.component.scss'],
})
export class ChangeHoldLocationDialogComponent implements OnInit {
  @Input() public pickupLocations: LocationWithName[];
  @Input() public hold: Hold;
  public holdLocationForm: FormGroup<HoldLocationForm>;
  public selectedLocationCode: string;
  public pickupAreas: PickupArea[] = [];
  public showPickupAreaFeature = false;
  public defaultPickupArea: PickupArea;
  public pickupAreasLoading = false;
  public readonly ilsType = IlsType;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly fb: FormBuilder,
    private readonly pickupLocationService: PickupLocationService,
    private readonly configLoader: ConfigurationLoader,
    private readonly featureToggleService: FeatureToggleService,
    private readonly translateService: TranslateService,
  ) {}

  public ngOnInit() {
    const isPickupAreaEnabled = this.featureToggleService.getToggles()['VEGAENG-179_2025_12_31_pickup_areas_updates'];
    const ilsType = this.configLoader.ilsType;
    this.showPickupAreaFeature = ilsType === IlsType.polaris && isPickupAreaEnabled;

    this.defaultPickupArea = {
      code: '',
      description: this.translateService.instant('selectPickupAreaPlaceholder'),
      parentCode: '',
      selected: false,
    };
    const currentLocation = this.findLocationByCode(this.hold.locationCode);
    this.selectedLocationCode = currentLocation ? currentLocation.code : this.pickupLocations[0].code;
    this.holdLocationForm = this.fb.group({
      pickupLocation: new FormControl(this.selectedLocationCode, Validators.required),
      pickupArea: new FormControl(null, Validators.required),
    });

    if (this.showPickupAreaFeature) {
      this.loadPickupAreas(this.selectedLocationCode);
    }
  }

  public onChangePickupLocation(pickupLocationCode: string) {
    if (!this.showPickupAreaFeature) return;

    delete this.hold.area; // on location change we do not want area pre-selection
    this.loadPickupAreas(pickupLocationCode);
  }

  public loadPickupAreas(pickupLocationCode: string) {
    this.pickupAreasLoading = true;
    this.pickupLocationService.getUserPickupArea(pickupLocationCode).subscribe((areas: PickupArea[]) => {
      this.pickupAreas = areas;
      this.holdLocationForm.get('pickupArea').reset();
      this.initializePickupAreaControl(this.pickupAreas);
      this.pickupAreasLoading = false;
    }, () => {
      this.pickupAreasLoading = false;
    });
  }

  get hasPickupAreaError(): boolean {
    return this.pickupAreas.length && this.holdLocationForm.get('pickupArea').invalid;
  }

  public onClose() {
    this.activeModal.dismiss();
  }

  public onSubmit() {
    const pickupLocation = this.findLocationByCode(this.holdLocationForm.value.pickupLocation);
    const pickupArea = this.holdLocationForm.value.pickupArea;
    this.activeModal.close({ pickupLocation, pickupArea });
  }

  public initializePickupAreaControl(areas: PickupArea[]): void {
    if (!areas || !areas.length) {
      return;
    }

    if (this.hold.area) {
      this.holdLocationForm.get('pickupArea').setValue(this.hold.area);
      return;
    }

    const noneOption = areas.find(a => a.description.toLowerCase().includes('none'));
    if (noneOption) {
      this.holdLocationForm.get('pickupArea').setValue(noneOption.code);
    } else {
      areas.unshift(this.defaultPickupArea);
      this.holdLocationForm.get('pickupArea').setValue(this.defaultPickupArea.code);
    }
  }

  private findLocationByCode(code: string): LocationWithName {
    return this.pickupLocations.find((loc) => loc.code === code);
  }
}
